import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';

import AmaiaHeader from './components/AmaiaHeader';
import AmaiaInput from './components/AmaiaInput';
import AmaiaWelcome from './components/AmaiaWelcome';
import ChatHistory from './components/ChatHistory';

import { retrieveAmaiaFaq, sendAmaiaMessageAction } from '../../../../redux/actions/index';
import { getCurrentTime, getStorefrontId } from './utils/amaia-helpers';

const EXPIRATION_TIME = 2 * 60 * 60 * 1000;
const SENDER_USER = 'user';
const SENDER_AMAIA = 'amaia';

const mapStateToProps = (state, ownProps) => {
  const { AmaiaChat, LanguageBundle, Manage, PantheonData, UserProfileSession } = state;
  let amaiaFaqChips = [];
  const faqChipsData = PantheonData ? PantheonData.amaiaFaqChips : null;

  if (faqChipsData) {
    if (faqChipsData.keywords && Array.isArray(faqChipsData.keywords.items)) {
      amaiaFaqChips = faqChipsData.keywords.items;
    } else if (Array.isArray(faqChipsData)) {
      amaiaFaqChips = faqChipsData;
    }
  }

  return {
    amaiaError: AmaiaChat.error,
    amaiaFaqChips,
    currentStorefront: Manage.storefront,
    helperTraslatedCopy: LanguageBundle.helperTraslatedCopy,
    isSignedIn: !!UserProfileSession.userFirstName,
    loadingAmaiaResponse: PantheonData ? PantheonData.loadingAmaiaResponse : false,
    storefrontId: getStorefrontId(ownProps, state),
    streamingMessage: (PantheonData && PantheonData.streamingMessage) || '',
    userFirstName: UserProfileSession.userFirstName,
  };
};

class AmaiaChat extends Component {
  constructor(props) {
    super(props);

    let initialMessages = [];

    try {
      const savedHistory = localStorage.getItem('amaia_chat_history');
      if (savedHistory) {
        const historyData = JSON.parse(savedHistory);
        if (historyData && Array.isArray(historyData.messages) && historyData.timestamp) {
          if (Date.now() - historyData.timestamp < EXPIRATION_TIME) {
            initialMessages = historyData.messages;
          } else {
            localStorage.removeItem('amaia_chat_history');
          }
        } else if (Array.isArray(historyData)) {
          initialMessages = historyData;
        } else {
          localStorage.removeItem('amaia_chat_history');
        }
      }
    } catch {
      localStorage.removeItem('amaia_chat_history');
    }

    const { loadingAmaiaResponse, streamingMessage } = props;
    let isTyping = false;
    let showChat = initialMessages.length > 0;

    // Recuperamos el contexto si el usuario recarga durante una respuesta activa.
    if (loadingAmaiaResponse) {
      showChat = true;
      isTyping = !streamingMessage;

      if (streamingMessage) {
        const lastMessage = initialMessages[initialMessages.length - 1];
        if (lastMessage && lastMessage.sender === SENDER_AMAIA) {
          lastMessage.text = streamingMessage;
        } else {
          initialMessages.push({
            id: Date.now(),
            sender: SENDER_AMAIA,
            text: streamingMessage,
            time: getCurrentTime(),
          });
        }
      }
    }

    this.state = {
      animation: false,
      inputValue: '',
      isTyping,
      messages: initialMessages,
      showChat,
    };
  }

  componentDidMount() {
    this.animationTimeout = setTimeout(() => {
      this.setState({ animation: true });
    }, 100);

    const { amaiaFaqChips, dispatch, storefrontId } = this.props;
    if ((!amaiaFaqChips || amaiaFaqChips.length === 0) && storefrontId) {
      dispatch(retrieveAmaiaFaq(storefrontId, 1));
    }

    window.addEventListener('storage', this.handleStorageChange);
  }

  componentDidUpdate(prevProps) {
    const { amaiaError, dispatch, loadingAmaiaResponse, storefrontId, streamingMessage, userFirstName } = this.props;

    // Borramos historial por privacidad si cambia el usuario.
    if (prevProps.userFirstName !== userFirstName) {
      localStorage.removeItem('amaia_chat_history');
      this.setState({ messages: [], showChat: false });
      return;
    }
    // Reiniciamos al cambiar de mercado para asegurar contenido en el idioma correcto.
    if (storefrontId && storefrontId !== prevProps.storefrontId) {
      dispatch(retrieveAmaiaFaq(storefrontId, 1));
      localStorage.removeItem('amaia_chat_history');
      this.setState({ messages: [], showChat: false });
    }
    if (amaiaError && amaiaError !== prevProps.amaiaError) {
      this.handleAmaiaError();
    }

    const wasLoading = prevProps.loadingAmaiaResponse;

    if (loadingAmaiaResponse && !wasLoading) {
      this.setState({ isTyping: true });
    } else if (!loadingAmaiaResponse && wasLoading) {
      const finalMessage = streamingMessage || prevProps.streamingMessage;

      if (finalMessage) {
        this.setState(prevState => ({
          isTyping: false,
          messages: [
            ...prevState.messages,
            {
              id: Date.now(),
              sender: SENDER_AMAIA,
              text: finalMessage,
              time: getCurrentTime(),
            },
          ],
        }), () => {
          this.saveHistory(this.state.messages);
        });
      } else {
        this.setState({ isTyping: false });
      }
    }
  }

  componentWillUnmount() {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    localStorage.removeItem('amaia_chat_history');
    window.removeEventListener('storage', this.handleStorageChange);
  }

  render() {
    const { amaiaFaqChips, currentStorefront, helperTraslatedCopy, isSignedIn, loadingAmaiaResponse, modalSearchClose, userFirstName } = this.props;
    const { animation, inputValue, isTyping, messages, showChat } = this.state;
    const isAIResponding = isTyping || loadingAmaiaResponse;

    const overlayClasses = cx('AmaiaModal-overlay', {
      'AmaiaModal-overlay--isOpen': animation,
    });

    const modalBodyClasses = cx('AmaiaModal-body', {
      'AmaiaModal-body--isOpen': animation,
      'AmaiaModal--isClosing': modalSearchClose,
    });

    return (
      <div className='AmaiaModal-container'>
        <div className='AmaiaModal-inner'>
          <div className={ overlayClasses } onClick={ this.closeModalWithAnimation } />
          <div className={ modalBodyClasses }>
            <div className='AmaiaChat-wrapper'>
              <AmaiaHeader
                onClose={ this.closeModalWithAnimation }
                showChat={ showChat }
              />
              { !showChat ? (
                <AmaiaWelcome
                  amaiaFaqChips={ amaiaFaqChips }
                  isAIResponding={ isAIResponding }
                  onChipClick={ this.handleChipClick }
                  userFirstName={ userFirstName }
                />
              ) : (
                <ChatHistory
                  currentStorefront={ currentStorefront }
                  isSignedIn={ isSignedIn }
                  isTyping={ isTyping }
                  messages={ messages }
                  userFirstName={ userFirstName }
                />
              ) }
              <AmaiaInput
                helperTraslatedCopy={ helperTraslatedCopy }
                inputValue={ inputValue }
                isAIResponding={ isAIResponding }
                onInputChange={ this.handleInputChange }
                onSendMessage={ this.handleSendMessage }
                showChat={ showChat }
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  handleStorageChange = (e) => {
    if (e.key === 'amaia_chat_history') {
      const savedHistory = e.newValue;
      if (savedHistory) {
        try {
          const historyData = JSON.parse(savedHistory);
          let newMessages = [];
          if (historyData && Array.isArray(historyData.messages)) {
            newMessages = historyData.messages;
          } else if (Array.isArray(historyData)) {
            newMessages = historyData;
          }

          this.setState({
            messages: newMessages,
            showChat: newMessages.length > 0,
          });
        } catch {
          this.setState({ messages: [], showChat: false });
          localStorage.removeItem('amaia_chat_history');
        }
      } else {
        this.setState({ messages: [], showChat: false });
      }
    }
  }

  handleInputChange = (value) => {
    this.setState({ inputValue: value });
  }

  handleChipClick = (text) => {
    if (this.state.isTyping || this.props.loadingAmaiaResponse) {
      return;
    }
    this.setState({ inputValue: text }, () => {
      this.handleSendMessage();
    });
  }

  handleSendMessage = () => {
    const { inputValue, isTyping, messages } = this.state;
    const userText = inputValue.trim();

    if (userText !== '' && !isTyping) {
      const newMessage = {
        id: Date.now(),
        sender: SENDER_USER,
        text: userText,
        time: getCurrentTime(),
      };

      const updatedMessages = [...messages, newMessage];

      this.setState({
        inputValue: '',
        isTyping: true,
        messages: updatedMessages,
        showChat: true,
      }, () => {
        this.saveHistory(this.state.messages);
        this.props.dispatch(sendAmaiaMessageAction(userText));
      });
    }
  }

  handleAmaiaError = () => {
    const { messages } = this.state;
    const lastMessage = messages[messages.length - 1];

    const errorMessage = {
      copyId: 'AMAIA_NO_RESULTS',
      id: Date.now(),
      sender: SENDER_AMAIA,
      text: '',
      time: getCurrentTime(),
    };

    const updatedMessages = [...messages];
    if (lastMessage && lastMessage.sender === SENDER_AMAIA) {
      updatedMessages[messages.length - 1] = errorMessage;
    } else {
      updatedMessages.push(errorMessage);
    }

    this.setState({ isTyping: false, messages: updatedMessages }, () => {
      this.saveHistory(updatedMessages);
    });
  }

  closeModalWithAnimation = () => {
    this.setState({ animation: false });
    this.closeTimeout = setTimeout(() => {
      if (this.props.handleCloseSearchModal) {
        this.props.handleCloseSearchModal();
      }
    }, 300);
  }

  saveHistory = (messages) => {
    try {
      const dataToSave = {
        messages,
        timestamp: Date.now(),
      };
      localStorage.setItem('amaia_chat_history', JSON.stringify(dataToSave));
    } catch (e) {
      // Si se excede el almacenamiento, conservamos solo los últimos 20 mensajes.
      if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
        try {
          const trimmedData = {
            messages: messages.slice(-20),
            timestamp: Date.now(),
          };
          localStorage.setItem('amaia_chat_history', JSON.stringify(trimmedData));
          return;
        } catch {
          // Fallback silencioso.
        }
      }
    }
  }
}

export default connect(mapStateToProps)(AmaiaChat);
